import type React from "react";
import { BLOCK_W, BLOCK_H } from "./types";

interface EdgeProps {
  id: string;
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  label?: string;
  isSelected?: boolean;
  /** Click handler (used to select the edge for deletion). */
  onClick?: () => void;
}

/** Cubic-bezier between two block centres. Source = bottom-mid of
 *  the source block; target = top-mid of the target block. Control
 *  points pulled vertically so curves stay neat for top-down layouts. */
export function Edge(props: EdgeProps): React.ReactElement {
  const sx = props.fromX + BLOCK_W / 2;
  const sy = props.fromY + BLOCK_H;
  const tx = props.toX + BLOCK_W / 2;
  const ty = props.toY;
  const dy = Math.max(40, (ty - sy) / 2);
  const c1x = sx;
  const c1y = sy + dy;
  const c2x = tx;
  const c2y = ty - dy;
  const path = `M ${sx} ${sy} C ${c1x} ${c1y}, ${c2x} ${c2y}, ${tx} ${ty}`;

  // Midpoint for the label.
  const mx = (sx + tx) / 2;
  const my = (sy + ty) / 2;

  const colour = props.isSelected ? "#2563eb" : "#64748b";
  const width = props.isSelected ? 2.5 : 1.5;

  return (
    <g style={{ cursor: props.onClick ? "pointer" : undefined }} onClick={props.onClick}>
      {/* Wide invisible hit target so clicking near the line works. */}
      <path d={path} stroke="transparent" strokeWidth={14} fill="none" />
      <path d={path} stroke={colour} strokeWidth={width} fill="none" markerEnd="url(#arrow)" />
      {props.label && (
        <g transform={`translate(${mx}, ${my})`}>
          <rect
            x={-22}
            y={-9}
            width={Math.max(44, props.label.length * 7 + 12)}
            height={18}
            rx={4}
            fill="#ffffff"
            stroke={colour}
            strokeWidth={1}
            transform={`translate(${-Math.max(44, props.label.length * 7 + 12) / 2 + 22}, 0)`}
          />
          <text
            x={0}
            y={4}
            textAnchor="middle"
            fontSize={11}
            fill={colour}
            style={{ pointerEvents: "none", userSelect: "none" }}
          >
            {props.label}
          </text>
        </g>
      )}
    </g>
  );
}

/** A small arrowhead marker shared by every edge. Mount once per
 *  SVG via <Edge.Defs />. */
export function EdgeDefs(): React.ReactElement {
  return (
    <defs>
      <marker
        id="arrow"
        viewBox="0 0 10 10"
        refX="10"
        refY="5"
        markerUnits="strokeWidth"
        markerWidth="8"
        markerHeight="8"
        orient="auto"
      >
        <path d="M 0 0 L 10 5 L 0 10 z" fill="#64748b" />
      </marker>
    </defs>
  );
}
